import cx from 'classnames';
import SebastianQuestions from '@/vdp/SebastianQuestions';
import SectionContainer from '@/vdp/SectionContainer';
import { sectionBottomPadding } from '@/vdp/VehicleDetailsContainer/styles';
import Section, { SectionId } from '~/constants/vdp/section';
import { useGetVehicleDetails } from '~/context/vdp/VehicleContext';
import useImageWithFallback from '~/hooks/common/ui/useImageWithFallback';
import useGetGeicoDiscount from '~/hooks/vdp/selectors/useGetGeicoDiscount';
import useGetWhyBuyDataLoaded from '~/hooks/vdp/selectors/useGetWhyBuyDataLoaded';
import useShowAboutPrice from '~/hooks/vdp/selectors/useShowAboutPrice';
import useWhyBuyData from '~/hooks/vdp/selectors/useWhyBuyData';
import useMarkSectionReady from '~/hooks/vdp/useMarkSectionReady';
import useSebastianQuestions from '~/hooks/vdp/useSebastianQuestions';
import useShowWhyBuyContent from '~/hooks/vdp/useShowWhyBuyContent';
import ExperimentWhyBuyCarousel from './ExperimentWhyBuyCarousel';
import useGetImages, { ImageType } from './hooks/useGetImages';
import WhyBuySkeleton from './SectionSkeleton';
import { Card } from './WhyBuy.styles';
import WhyBuyVehicleImage from './WhyBuyVehicleImage';
import { SectionHeader } from '../styles';

const ExperimentWhyBuy = () => {
  const {
    make = '',
    parentModel = '',
    trim = '',
    year = ''
  } = useGetVehicleDetails();
  const { summary = '' } = useWhyBuyData();
  const { isLoaded } = useGetWhyBuyDataLoaded();
  const showWhyBuyContent = useShowWhyBuyContent();
  const { whyBuyImageMap, initialImageType } = useGetImages();
  const image = useImageWithFallback<ImageType>(
    whyBuyImageMap,
    initialImageType,
    'whyBuy'
  );
  const { showAboutPrice } = useShowAboutPrice();
  const geicoDiscount = useGetGeicoDiscount();
  const { isLoaded: isSebastianLoaded } = useSebastianQuestions(Section.WhyBuy);
  useMarkSectionReady(SectionId.WhyBuy, isLoaded && isSebastianLoaded);
  const hasImage = Boolean(image);
  const vehicleName = [year, make, parentModel, trim].filter(Boolean).join(' ');

  if (!isLoaded) return <WhyBuySkeleton />;

  if (!showWhyBuyContent) return null;

  return (
    <SectionContainer
      className={cx(
        'flex flex-col gap-4xl',
        'scroll-mt-7xl sm:scroll-mt-[70px]',
        // when has image and above pickup and delivery
        hasImage && !showAboutPrice && !geicoDiscount
          ? 'pb-8xl md:pb-[104px]'
          : sectionBottomPadding,
        {
          // when no image and above about the price or geico banner
          'pb-0 sm:pb-5xl md:pb-7xl':
            !hasImage && (showAboutPrice || geicoDiscount)
        }
      )}
      section={Section.WhyBuy}
    >
      <div className="flex flex-col">
        <Card hasImage={hasImage}>
          <div className="flex flex-col md:gap-2xl">
            <div className="flex flex-col gap-xl">
              <SectionHeader
                className="relative z-1 !overflow-visible px-2xl md:px-4xl"
                data-testid="why-buy-header"
              >
                <span>Why You’ll Love This {parentModel}</span>
              </SectionHeader>
              <p className="px-2xl text-center text-text-default t-body-sm md:px-4xl lg:px-8xl">
                {summary}
              </p>
            </div>
            <ExperimentWhyBuyCarousel />
          </div>
          <WhyBuyVehicleImage image={image} />
        </Card>
        <p
          className={cx(
            'px-2xl text-center text-text-disabled t-body-xs md:px-3xl lg:px-8xl',
            hasImage ? 'mt-3xl md:mt-8xl' : 'mt-2xl'
          )}
          data-qa="why-buy-ai-disclaimer"
        >
          We use stock photos and AI-generated descriptions to highlight
          features on this {vehicleName}. Explore our photo gallery and key
          features above for more details.
        </p>
      </div>

      <SebastianQuestions
        className={cx('px-2xl sm:px-0', image ? 'md:pt-5xl' : 'md:pt-2xl')}
        section={Section.WhyBuy}
      />
    </SectionContainer>
  );
};

export default ExperimentWhyBuy;
